import React, { useContext, useState, useEffect, useRef, createRef } from 'react';
import { type Property } from 'csstype';
import { NavContext } from '@/context/nav-context';
import clsx from 'clsx';
import MobileTopNav from './mobile-top-nav';
import MobileSubNav from './mobile-sub-nav';
import { KEY } from '@/data/constants';

// utils
import { isInViewport } from 'Utils/utils';

// styles
import styles from './hamburger-nav.module.scss';
import { focusIfHTMLElement } from '@/utils';
import Notification from '@/components/shared/notification/notification';
import { useIntl } from 'react-intl';
import { LayoutContext } from '@/context/layout-context';
import { useCommonContextProps } from '@/hooks';
import { useStore } from '@/store/useStore';

const HamburgerNav: React.FC = () => {
  // shared state
  const {
    nav: {
      ['isMobileNavOpen']: [isMobileNavOpen, setIsMobileNavOpen],
      ['isAnimating']: [, setIsAnimating],
    },
  } = useContext(NavContext);
  const { locale } = useCommonContextProps();
  const data = useStore(({ headerFooter }) => headerFooter);

  const mobileNavRef = useRef<HTMLElement>(null);
  const mobileSubNavRefs = useRef<React.MutableRefObject<HTMLUListElement>[]>([]);

  // menu state
  const [currentIndex, setIndex] = useState(0);
  const numberOfSlides = 4;
  const [close, setClose] = useState(false);
  const [menuOverflow, setMenuOverflow] = useState<Property.OverflowY>('scroll');

  const setCurrentIndex = (index: number): void => {
    setIndex(index);
    setTimeout(
      () => {
        focusIfHTMLElement(mobileSubNavRefs?.current[index]?.current?.children[0]);
      },
      process.env.NODE_ENV === 'test' ? 0 : 500
    );
  };

  const closeNav = () => {
    setClose(!close);
    setCurrentIndex(0);
  };

  const onAnimationStart = () => {
    setIsAnimating(true);
  };

  const onAnimationEnd = () => {
    setIsAnimating(false);
    if (close) toggleMobileNav();
    setClose(false);
  };

  // set state for if mobile nav is open or closed
  const toggleMobileNav = () => setIsMobileNavOpen(!isMobileNavOpen);

  // detect which menu is in viewport on transitionEnd event and if menu is longer than viewport then set overflow-y to hidden or scroll
  const menuIds = ['hamburger__topnav', 'hamburger__subnavL0', 'hamburger__subnavL1', 'hamburger__subnavL2'];
  const onTransitionEnd = () => {
    setMenuOverflow(isInViewport(document.getElementById(menuIds[currentIndex])) ? 'hidden' : 'scroll');
  };

  // TODO: figure out what to do about account links
  // const { data: accountLinksData } = useQuery(GET_ACCOUNT_LINKS, {
  //   skip: userStatus === 'anonymous',
  // });

  const keydownHandler = (event: KeyboardEvent) => {
    if (event.key === KEY.TAB) return;
    event = event || window.event;

    const key = event.key || event.type;
    if (event.key !== KEY.ENTER) event.preventDefault();

    switch (key) {
      case KEY.ARROW_LEFT:
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
        break;
      case KEY.ESCAPE:
        closeNav();
        break;
    }
  };

  mobileSubNavRefs.current = Array(4)
    .fill(undefined)
    .map((_, index) => mobileSubNavRefs.current[index] || createRef());

  useEffect(() => {
    const ref = mobileNavRef.current;
    if (!ref) return;

    // if (mobileSubNavRefs.current) mobileSubNavRefs?.current[0]?.current?.children[0]?.children[0]?.focus();

    ref.addEventListener('keydown', keydownHandler);
    return () => {
      ref.removeEventListener('keydown', keydownHandler);
    };
  });

  if (!data) return <></>;

  const navData = data[locale];

  return (
    <nav
      className={clsx(styles.hamburgerNav)}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}
      style={isMobileNavOpen ? { display: 'block' } : { display: 'none' }}
      ref={mobileNavRef}
    >
      <div className={styles.lightbox}></div>
      <div className={clsx(styles.navContainer, isMobileNavOpen && styles.slideIn, close && styles.slideOut)}>
        <div id="carouselOuterContainer" className={styles.carouselOuterContainer} style={{ overflowY: menuOverflow }}>
          <div
            className={styles.carouselInnerContainer}
            style={{
              transform: `translateX(-${(currentIndex * 100) / numberOfSlides}%)`,
              width: `${numberOfSlides * 100}%`,
            }}
            onTransitionEnd={onTransitionEnd}
          >
            <MobileTopNav
              navigationMobile={navData.navigationMobile}
              level="topnav"
              setCurrentIndex={setCurrentIndex}
              ref={mobileSubNavRefs.current[0]}
            />
            <MobileSubNav
              navigationMobile={navData.navigationMobile}
              accountLinks={[]}
              level="L0"
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              ref={mobileSubNavRefs.current[1]}
            />
            <MobileSubNav
              navigationMobile={navData.navigationMobile}
              accountLinks={[]}
              level="L1"
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              ref={mobileSubNavRefs.current[2]}
            />
            <MobileSubNav
              navigationMobile={navData.navigationMobile}
              accountLinks={[]}
              level="L2"
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              ref={mobileSubNavRefs.current[3]}
            />
          </div>
        </div>
        <button id="mobileNavCloseButton" className={styles.closeButton} onClick={closeNav} tabIndex={0}>
          <span className={styles.iconCross}></span>
        </button>
      </div>
    </nav>
  );
};

export default HamburgerNav;
